import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import PiSymbol from "@/components/PiSymbol";

const navItems = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0a0a0a]/85 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <PiSymbol size={36} />
          <div className="leading-tight">
            <div className="font-display text-xl font-bold tracking-wide">
              Pie Solutions
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#39FF14]/80">
              For Future Factories
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `relative px-4 py-2 font-display uppercase tracking-[0.22em] text-sm transition-colors ${
                  isActive
                    ? "text-[#39FF14]"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta-share-bom"
            className="ml-4 btn-primary text-xs"
          >
            Share Your BOM
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#0a0a0a]/95 backdrop-blur-xl">
          <div className="flex flex-col px-6 py-4 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `font-display uppercase tracking-[0.22em] text-sm py-3 ${
                    isActive ? "text-[#39FF14]" : "text-white/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-mobile-cta"
              className="btn-primary w-fit mt-2 text-xs"
            >
              Share Your BOM
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
